import { formatReceivedTime, getOrderTiming, parseReceivedTime } from "./orderTiming";
import { isLiveTask } from "./kitchenActions";

const suppliedName = (...values) => values.find((value) => typeof value === "string" && value.trim())?.trim() || "";
const uniqueNames = (values) => [...new Set(values.filter(Boolean))];

export const tablePendingJobs = (table) => (table?.jobs || []).filter((job) =>
  job?.jobId && (job?.tasks || []).some((task) =>
    isLiveTask(task) && Number(task?.quantity) > 0 && (task?.status || task?.taskStatus || job?.status) === "PENDING"));

export const kitchenOrderPeople = (job) => ({
  captain: suppliedName(job?.captainName, job?.metadata?.captainName, job?.waiterName, job?.metadata?.waiterName),
  customer: suppliedName(job?.customerName, job?.metadata?.customerName, job?.metadata?.userName),
});

export const flattenTableCatalogue = (areas) => {
  const tables = new Map();
  areas.forEach(({ floor, area, tables: children }) => children.forEach((table) => {
    // fetch-by-parent sends `metadata`; the layout hierarchy sends the same data as `tableMetadata`.
    const metadata = table.metadata || table.tableMetadata || {};
    const id = metadata.tableMetadataId ?? table.mapId;
    if (id == null) return;
    const key = String(id);
    // `tableName` is the label staff see (e.g. "T1"); `tableNumber` is an internal serial code (e.g. "TABLE265").
    const name = [metadata.tableName, table.entityName, metadata.tableNumber, table.tableNumber, id]
      .find((value) => value != null && String(value).trim());
    tables.set(key, {
      key,
      aliases: [key, String(table.mapId ?? "")].filter(Boolean),
      name: String(name ?? id),
      areaId: String(area.mapId),
      areaName: area.entityName || "",
      floorName: floor.entityName || "",
      status: table.status,
      cartTableId: metadata.tableMetadataId == null ? null : String(metadata.tableMetadataId),
      occupancyStartedAt: null,
      captainName: suppliedName(table.captainName, metadata.captainName, metadata.waiterName),
      customerName: suppliedName(table.customerName, metadata.customerName),
      jobs: [],
    });
  }));
  return [...tables.values()];
};

// Customer time starts with the current cart, not a kitchen job or layout creation.
export const enrichTableOccupancy = (catalogue, cartStatuses) => catalogue.map((table) => {
  if (table.status !== "OCCUPIED") return { ...table, occupancyStartedAt: null };
  const current = cartStatuses.find((cart) => String(cart.tableId) === table.cartTableId);
  if (!current) return { ...table, occupancyStartedAt: null };
  if (current.cartStatus === "CLOSED") return { ...table, status: "AVAILABLE", occupancyStartedAt: null };
  return { ...table, occupancyStartedAt: current.createdDate ?? null };
});

export const getTableOccupancy = (table, now = Date.now()) => {
  const kind = { OCCUPIED: "occupied", AVAILABLE: "available", RESERVED: "reserved" }[table.status] || "unknown";
  const label = { occupied: "Occupied", available: "Available", reserved: "Reserved", unknown: "Occupancy unavailable" }[kind];
  const started = kind === "occupied" ? parseReceivedTime(table.occupancyStartedAt) : null;
  let durationLabel = table.occupancyLoading ? "Loading…" : "—";
  if (started != null && Number.isFinite(now)) {
    const minutes = Math.floor(Math.max(0, now - started) / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    durationLabel = days ? `${days}d ${hours % 24}h ${minutes % 60}m`
      : hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
  }
  return { kind, label, durationLabel };
};

export const buildTableWorkload = (catalogue, jobs, now = Date.now()) => {
  const tables = catalogue.map((table) => ({ ...table, jobs: [] }));
  jobs.forEach((job) => {
    const metadata = job.metadata || {};
    const id = metadata.tableId ?? metadata.tableMapId ?? metadata.tableMetadataId;
    // Only map dine-in jobs; delivery/takeaway must not appear as table 1.
    if (["TAKEAWAY", "DELIVERY"].includes(String(id).toUpperCase()) ||
      (metadata.subOrderType && !String(metadata.subOrderType).includes("DINE_IN"))) return;
    let table = id != null && tables.find((candidate) => candidate.aliases.includes(String(id)));
    if (!table && metadata.tableNumber != null) {
      const matches = tables.filter((candidate) =>
        candidate.name === String(metadata.tableNumber) &&
        (!metadata.layoutName || candidate.areaName === metadata.layoutName || candidate.floorName === metadata.layoutName) &&
        (!metadata.layoutId || candidate.areaId === String(metadata.layoutId)));
      if (matches.length === 1) table = matches[0];
    }
    if (!table && (id != null || metadata.tableNumber != null)) {
      // Keep live jobs visible when their table has been removed from the catalogue.
      const key = `unmapped:${id ?? metadata.layoutId ?? metadata.layoutName}:${metadata.tableNumber ?? ""}`;
      table = tables.find((candidate) => candidate.key === key);
      if (!table) {
        table = { key, aliases: [], name: String(metadata.tableNumber ?? id),
          areaName: metadata.layoutName || "Unmapped table", floorName: "", jobs: [] };
        tables.push(table);
      }
    }
    if (table && job.tasks?.some(isLiveTask)) {
      // The layout label may differ from the kitchen API's current table number.
      if (metadata.tableNumber != null && String(metadata.tableNumber).trim()) {
        table.name = String(metadata.tableNumber).trim();
      }
      table.jobs.push(job);
    }
  });
  const rank = { neutral: 0, green: 1, orange: 2, red: 3 };
  return tables.map((table) => {
    let pendingQty = 0;
    let processingQty = 0;
    let earliest = null;
    let timing = { tone: "neutral", label: "No active kitchen orders", display: "—", description: "" };
    table.jobs.forEach((job) => {
      const received = parseReceivedTime(job.created);
      if (received != null && (earliest == null || received < earliest)) earliest = received;
      const jobTiming = getOrderTiming({ created: job.created, prepTime: job.metadata?.avgPrepTime }, now);
      const deadline = received == null || jobTiming.prepMinutes == null
        ? Infinity : received + jobTiming.prepMinutes * 60000;
      if (rank[jobTiming.tone] > rank[timing.tone] ||
        (jobTiming.tone === timing.tone && jobTiming.tone !== "neutral" &&
          deadline < (timing.deadline ?? Infinity))) timing = { ...jobTiming, deadline };
      (job.tasks || []).filter(isLiveTask).forEach((task) => {
        const qty = Math.max(0, Number(task.quantity) || 0);
        if ((task.status || job.status) === "PENDING") pendingQty += qty;
        else if ((task.status || job.status) === "IN_PROGRESS") processingQty += qty;
      });
    });
    if (table.jobs.length && timing.tone === "neutral") timing.label = "Timing unavailable";
    const people = table.jobs.map(kitchenOrderPeople);
    return { ...table, occupancy: getTableOccupancy(table, now), timing, pendingQty, processingQty,
      captainNames: uniqueNames([suppliedName(table.captainName), ...people.map((person) => person.captain)]),
      customerNames: uniqueNames([suppliedName(table.customerName), ...people.map((person) => person.customer)]),
      receivedLabel: earliest == null ? "—" : formatReceivedTime(earliest) };
  });
};
